/* Portfolio lightbox: pinch, pan and double-tap zoom.

   Replaces the gallery block's built-in zoom, which had two problems on touch:
   touchend called toggleZoom and then the browser's synthesised click called it
   again, so a tap zoomed in and straight back out; and zooming ignored where
   you tapped, so the point of interest jumped away from your finger.

   This listens on document in the capture phase and stops events that target
   the lightbox image from reaching the block's own handlers. Nothing else in
   the lightbox is affected: the backdrop, arrows, close button and the
   "available on" cards all keep working normally.

   Zoom is anchored to the midpoint of your fingers (or the cursor), so the
   detail you are looking at stays under them. Panning is clamped so the
   artwork cannot be flung off screen.

   Clamping measures the stage's content box rather than its border box, so the
   strip reserved at the foot of the stage for the "available on" cards is
   respected. Measuring the border box instead re-centred the artwork over that
   strip on the very first touch, dropping the picture down behind the cards. */
(function () {
  "use strict";

  var MIN = 1;
  var MAX = 5;
  var DOUBLE_TAP_MS = 300;
  var DOUBLE_TAP_SLOP = 30;
  var REDUCED = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  var img = null;        // the image currently being manipulated
  var base = null;       // its untransformed rect, measured once per image
  var stage = null;      // the rect we clamp against
  var scale = 1, tx = 0, ty = 0;
  var pointers = new Map();
  var pinchStart = null;
  var panStart = null;
  var lastTap = 0, lastTapX = 0, lastTapY = 0;
  var moved = false;
  var lastInputType = "";

  function isLightboxImg(el) {
    return el && el.nodeType === 1 && el.matches && el.matches("[data-lightbox-img]");
  }

  function stageOf(el) {
    return el.closest("[data-lightbox-stage]") || el.parentElement;
  }

  // The area the artwork may occupy: the stage minus its padding. That padding
  // is where the "available on" cards sit, so clamping against the full border
  // box would let the picture settle underneath them.
  function contentRect(el) {
    var r = el.getBoundingClientRect();
    var cs = window.getComputedStyle(el);
    var pt = parseFloat(cs.paddingTop) || 0;
    var pb = parseFloat(cs.paddingBottom) || 0;
    var pl = parseFloat(cs.paddingLeft) || 0;
    var pr = parseFloat(cs.paddingRight) || 0;
    return {
      left: r.left + pl,
      top: r.top + pt,
      width: r.width - pl - pr,
      height: r.height - pt - pb
    };
  }

  // Measure the image where it sits with no transform applied. Everything else
  // is computed against this, so the maths stays stable while zoomed.
  function measure(el) {
    var prev = el.style.transform;
    el.style.transition = "none";
    el.style.transform = "none";
    var r = el.getBoundingClientRect();
    var s = contentRect(stageOf(el));
    el.style.transform = prev;
    return { base: { left: r.left, top: r.top, width: r.width, height: r.height }, stage: s };
  }

  function attach(el) {
    if (img === el && base) return;
    img = el;
    el.style.transformOrigin = "0 0";
    var m = measure(el);
    base = m.base;
    stage = m.stage;
    scale = 1; tx = 0; ty = 0;
    apply(false);
  }

  function clamp() {
    var w = base.width * scale;
    var h = base.height * scale;
    var offL = base.left - stage.left;
    var offT = base.top - stage.top;

    if (w <= stage.width) {
      tx = (stage.width - w) / 2 - offL;
    } else {
      var maxTx = -offL;
      var minTx = stage.width - w - offL;
      if (tx > maxTx) tx = maxTx;
      if (tx < minTx) tx = minTx;
    }

    if (h <= stage.height) {
      ty = (stage.height - h) / 2 - offT;
    } else {
      var maxTy = -offT;
      var minTy = stage.height - h - offT;
      if (ty > maxTy) ty = maxTy;
      if (ty < minTy) ty = minTy;
    }
  }

  function apply(animate) {
    if (!img) return;
    clamp();
    img.style.transition = (animate && !REDUCED) ? "transform 260ms cubic-bezier(0.22, 1, 0.36, 1)" : "none";
    img.style.transform = "translate(" + tx + "px, " + ty + "px) scale(" + scale + ")";
    img.style.cursor = scale > 1 ? "grab" : "zoom-in";
  }

  // Zoom about a screen point, so whatever is under the fingers stays there.
  function zoomTo(next, cx, cy, animate) {
    if (next < MIN) next = MIN;
    if (next > MAX) next = MAX;
    var px = cx - base.left;
    var py = cy - base.top;
    tx = px - (px - tx) * next / scale;
    ty = py - (py - ty) * next / scale;
    scale = next;
    apply(animate);
  }

  function reset() {
    if (!img) return;
    scale = 1; tx = 0; ty = 0;
    img.style.transition = "none";
    img.style.transform = "none";
    img.style.cursor = "zoom-in";
    base = null;
    img = null;
    pointers.clear();
    lastTap = 0;
    moved = false;
    pinchStart = null;
    panStart = null;
  }

  function dist(a, b) {
    var dx = a.x - b.x, dy = a.y - b.y;
    return Math.sqrt(dx * dx + dy * dy);
  }
  function mid(a, b) {
    return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
  }
  function two() {
    var it = pointers.values();
    return [it.next().value, it.next().value];
  }

  document.addEventListener("pointerdown", function (e) {
    if (!isLightboxImg(e.target) || (e.pointerType === "mouse" && e.button !== 0)) return;
    lastInputType = e.pointerType;
    e.stopPropagation();
    e.preventDefault();
    attach(e.target);
    try { e.target.setPointerCapture(e.pointerId); } catch (err) {}
    pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
    moved = pointers.size > 1;

    if (pointers.size === 2) {
      var p = two();
      pinchStart = { d: dist(p[0], p[1]), scale: scale, mid: mid(p[0], p[1]), tx: tx, ty: ty };
      lastTap = 0;
      panStart = null;
    } else if (pointers.size === 1) {
      panStart = { x: e.clientX, y: e.clientY, tx: tx, ty: ty };
    }
  }, true);

  document.addEventListener("pointermove", function (e) {
    if (!pointers.has(e.pointerId)) return;
    e.stopPropagation();
    e.preventDefault();
    pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });

    if (pointers.size >= 2 && pinchStart) {
      var p = two();
      var d = dist(p[0], p[1]);
      if (!d || !pinchStart.d) return;
      var m = mid(p[0], p[1]);
      var next = pinchStart.scale * (d / pinchStart.d);
      if (next < MIN) next = MIN;
      if (next > MAX) next = MAX;
      tx = m.x - base.left - (pinchStart.mid.x - base.left - pinchStart.tx) * next / pinchStart.scale;
      ty = m.y - base.top - (pinchStart.mid.y - base.top - pinchStart.ty) * next / pinchStart.scale;
      scale = next;
      apply(false);
      moved = true;
      return;
    }

    if (panStart && (Math.abs(e.clientX - panStart.x) > 6 || Math.abs(e.clientY - panStart.y) > 6)) moved = true;
    if (panStart && scale > 1) {
      var dx = e.clientX - panStart.x;
      var dy = e.clientY - panStart.y;
      if (Math.abs(dx) > 3 || Math.abs(dy) > 3) moved = true;
      tx = panStart.tx + dx;
      ty = panStart.ty + dy;
      apply(false);
      if (img) img.style.cursor = "grabbing";
    }
  }, true);

  function endPointer(e) {
    if (!pointers.has(e.pointerId)) return;
    e.stopPropagation();
    pointers.delete(e.pointerId);

    if (pointers.size < 2) pinchStart = null;
    if (pointers.size === 1) {
      var rest = pointers.values().next().value;
      panStart = { x: rest.x, y: rest.y, tx: tx, ty: ty };
    }

    if (pointers.size === 0) {
      panStart = null;
      if (moved || e.type === "pointercancel") lastTap = 0;

      // A clean tap with no drag: double-tap zooms in at that point, or back
      // out if already zoomed. A single tap does nothing, so the image no
      // longer flickers in and out.
      if (!moved && img && e.type !== "pointercancel" && e.pointerType !== "mouse") {
        var t = Date.now();
        var near = Math.abs(e.clientX - lastTapX) < DOUBLE_TAP_SLOP &&
                   Math.abs(e.clientY - lastTapY) < DOUBLE_TAP_SLOP;
        if (lastTap && t - lastTap < DOUBLE_TAP_MS && near) {
          zoomTo(scale > 1.05 ? MIN : 2.6, e.clientX, e.clientY, true);
          lastTap = 0;
          return;
        } else {
          lastTap = t;
          lastTapX = e.clientX;
          lastTapY = e.clientY;
        }
      }

      // Settle back if a pinch ended below the resting size.
      if (scale <= MIN + 0.02 && scale !== MIN) {
        scale = MIN;
        apply(true);
      } else {
        apply(false);
      }
      if (img) img.style.cursor = scale > 1 ? "grab" : "zoom-in";
    }
  }

  document.addEventListener("pointerup", endPointer, true);
  document.addEventListener("pointercancel", endPointer, true);

  // Swallow the synthesised click so the block's own toggle never runs. This
  // is the specific cause of the zoom-in-then-out flash.
  document.addEventListener("click", function (e) {
    if (!isLightboxImg(e.target)) return;
    e.stopPropagation();
    e.preventDefault();
  }, true);

  document.addEventListener("dblclick", function (e) {
    if (!isLightboxImg(e.target)) return;
    e.stopPropagation();
    e.preventDefault();
    if (lastInputType === "touch" || (e.sourceCapabilities && e.sourceCapabilities.firesTouchEvents)) return;
    attach(e.target);
    zoomTo(scale > 1.05 ? MIN : 2.6, e.clientX, e.clientY, true);
  }, true);

  // Trackpad and mouse wheel zoom, anchored at the cursor.
  document.addEventListener("wheel", function (e) {
    if (!isLightboxImg(e.target)) return;
    e.stopPropagation();
    e.preventDefault();
    attach(e.target);
    var factor = Math.exp(-e.deltaY * 0.0022);
    zoomTo(scale * factor, e.clientX, e.clientY, false);
  }, { capture: true, passive: false });

  // Swallow the legacy touch events too, so the block's touch handlers stay
  // out of the way on browsers that fire both.
  ["touchstart", "touchmove", "touchend"].forEach(function (type) {
    document.addEventListener(type, function (e) {
      if (!isLightboxImg(e.target)) return;
      e.stopPropagation();
    }, true);
  });

  // Reset when the picture changes or the lightbox closes, and re-measure on
  // resize or rotation since the base rect moves.
  function watch(el) {
    var mo = new MutationObserver(function () { reset(); });
    mo.observe(el, { attributes: true, attributeFilter: ["src"] });
  }

  var seen = new WeakSet();
  function scan(top) {
    top = top && top.querySelectorAll ? top : document;
    var images = Array.from(top.querySelectorAll("[data-lightbox-img]"));
    if (top.matches && top.matches("[data-lightbox-img]")) images.unshift(top);
    images.forEach(function (el) {
      if (seen.has(el)) return;
      seen.add(el);
      el.style.cursor = "zoom-in";
      watch(el);
    });
    var boxes = Array.from(top.querySelectorAll("[data-lightbox]"));
    if (top.matches && top.matches("[data-lightbox]")) boxes.unshift(top);
    boxes.forEach(function (box) {
      if (seen.has(box)) return;
      seen.add(box);
      var mo = new MutationObserver(function () {
        if (!box.classList.contains("active")) reset();
      });
      mo.observe(box, { attributes: true, attributeFilter: ["class"] });
    });
  }

  new MutationObserver(function (mutations) {
    mutations.forEach(function (mutation) {
      mutation.addedNodes.forEach(function (node) {
        if (node.nodeType === 1 && node.isConnected) scan(node);
      });
    });
  }).observe(document.body, { childList: true, subtree: true });
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", scan);
  } else {
    scan();
  }

  window.addEventListener("resize", function () { reset(); });
  window.addEventListener("orientationchange", function () { reset(); });
})();
